/**
 * useEventThread — data-layer для обсуждения события
 * (livestream / live_room / offline_event).
 *
 * CORE-620 — migrated off GetStream onto Agape.ChatApi. The model is unchanged:
 *   Master = Circle = one channel keyed `master-circle-{masterProfileId}`.
 *   Event  = an event-card message in that channel (custom `eventId`).
 *   Обсуждение = messages that quote (reply to) the event card.
 * Reads/writes now ride `ChatApiChannel` (SignalR hub) — the same transport
 * `components/chat/EventThread.tsx` already uses for event-thread discussions.
 * No Stream client is touched.
 *
 * Consumers: `LiveCommentsPanel` (the always-visible comments column on the
 * event-detail page) and `LiveAnnouncementHero` (type-only `EventKind`).
 *
 * Behaviour parity notes vs the old Stream hook:
 *   - The event card is created by the backend (event-thread cutover, CORE-605),
 *     so we no longer client-seed a parent message. CORE-620 (актуализация
 *     27.10): карточка ищется и за пределами новейших 50 сообщений канала; не
 *     нашлась — статус `unavailable`, и писать некуда (было «готово» с пустым
 *     корнем, и отправка падала).
 *   - `canWrite` is membership from `api.getMasterCircle().isMember` instead of
 *     Stream channel membership; the join flow (subscribe + reloadTick) still
 *     flips it exactly as before.
 *   - CORE-1725 — членство теперь перечислено (`membership`, см.
 *     utils/eventDiscussionAccess): владелец круга пишет без подписки и CTA
 *     подписки не видит, а «ещё не знаем» не равно «не участник».
 *   - `participants` / `participantsCount` aren't exposed by the ChatApi read
 *     model here, so they're empty (LiveCommentsPanel doesn't render them).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { ChatApiChannel } from '../services/chat/chatApiChannel';
import type { ThreadMessage } from '../services/chat/chatTypes';
import { EVENT_THREAD_CHANNEL_PREFIX } from '../constants/chat';
import { resolveName, FALLBACK_GUEST } from '../utils/displayName';
import {
  canPostToDiscussion,
  resolveDiscussionMembership,
  type DiscussionMembership,
  type DiscussionReadiness,
} from '../utils/eventDiscussionAccess';
import { eventCardMatcher, locateEventCard } from '../utils/eventCardSearch';

export type EventKind = 'livestream' | 'live_room' | 'offline_event';

export type EventMeta = {
  liveId: number;
  masterProfileId: number;
  /**
   * CORE-620 (актуализация 27.10) — внешний id карточки обсуждения из ответа
   * эфира (`LiveDetailResponse.discussionId`, он же `Live.StreamMessageId`).
   * `null` — сервер прямо сказал «карточки нет» (CORE-1764, CORE-1283), искать
   * нечего. `undefined` — не знаем, ищем по `eventId`.
   */
  discussionId?: string | null;
  eventKind: EventKind;
  title: string;
  description?: string | null;
  coverUrl?: string | null;
  startAtUtc: string;
  durationMinutes?: number | null;
  deeplink?: string;
};

export type EventThreadParticipant = {
  id: string;
  name: string;
  photoUrl: string | null;
};

export type EventThreadMessage = {
  id: string;
  author: string;
  photoUrl: string | null;
  text: string;
  timestamp: string;
  isHost: boolean;
  isOwn: boolean;
};

/**
 * `ready` — карточка события найдена, отвечать есть куда. `unavailable` — канал
 * открыт, а карточки нет (не заведена или не нашлась): обсуждения нет, и
 * писать в него нельзя (CORE-620, актуализация 27.10).
 */
export type EventThreadStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error' | 'disabled';

export type UseEventThreadResult = {
  status: EventThreadStatus;
  error: string | null;

  /** Deterministic ChatApi channel key `master-circle-{id}` (null until meta). */
  channelKey: string | null;
  /** Id of the event-card message this thread hangs off (null if none yet). */
  parentMessageId: string | null;
  /** Reply messages, newest last. Already filtered to replies of the card. */
  messages: EventThreadMessage[];
  /** Pinned/host preview above the thread (the event card body, if any). */
  pinnedMessage: EventThreadMessage | null;

  participants: EventThreadParticipant[];
  participantsCount: number;

  /** `true` if the event has ended and the thread is read-only. Always false. */
  isFrozen: boolean;
  /** `true` if current user can post replies (owner or member of the master's circle). */
  canWrite: boolean;
  /** `true` if current user is the master who owns this circle. */
  isOwner: boolean;
  /** CORE-1725 — кем зритель приходится кругу; `'unknown'`, пока сервер не ответил. */
  membership: DiscussionMembership;
  /** CORE-620 — есть ли карточка, к которой крепятся ответы. */
  readiness: DiscussionReadiness;

  sendMessage: (text: string) => Promise<void>;
};

const channelKeyFor = (masterProfileId: number) =>
  `${EVENT_THREAD_CHANNEL_PREFIX}${masterProfileId}`;

const formatTimestamp = (date: Date): string => {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const customOf = (m: ThreadMessage): Record<string, any> => ((m.raw as any)?.custom || {});

const toThreadMessage = (
  m: ThreadMessage,
  cardAuthorId: string | undefined,
): EventThreadMessage => ({
  id: m.id,
  author: resolveName(m.senderName, FALLBACK_GUEST),
  photoUrl: m.senderAvatar ?? null,
  text: m.text || '',
  timestamp: formatTimestamp(m.createdAt),
  // The event card is host-authored; a reply from that same author is the host.
  isHost: !!cardAuthorId && m.senderId === cardAuthorId,
  isOwn: m.isMine,
});

export const useEventThread = (
  meta: EventMeta | null,
  opts: { enabled?: boolean; isRsvped?: boolean; reloadTick?: number } = {},
): UseEventThreadResult => {
  const { user } = useAuth();
  const myAppId = user?.id || '';
  const enabled = opts.enabled !== false && !!meta && !!myAppId;
  // Callers bump this after a successful subscribeToMaster to force a reload so
  // `canWrite` flips once backend membership lands.
  const reloadTick = opts.reloadTick ?? 0;

  const [status, setStatus] = useState<EventThreadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [allMessages, setAllMessages] = useState<ThreadMessage[]>([]);
  const [parentMessageId, setParentMessageId] = useState<string | null>(null);
  // CORE-1725 — ответ о членстве помечен тем, про кого он: смена зрителя или
  // мастера не наследует чужой ответ, а повторный запрос после подписки
  // (reloadTick) не стирает уже известный — поле ввода не мигает.
  const [memberAnswer, setMemberAnswer] = useState<{ key: string; isMember: boolean | null } | null>(null);

  const chatApiRef = useRef<ChatApiChannel | null>(null);
  const threadKeyRef = useRef<string | null>(null);

  const channelKey = meta ? channelKeyFor(meta.masterProfileId) : null;
  const liveId = meta?.liveId ?? null;
  const discussionKey = meta?.discussionId;

  // ─── connect to the ChatApi channel + load membership ──────────
  useEffect(() => {
    if (!enabled || !meta || !channelKey) {
      setStatus('idle');
      setAllMessages([]);
      setParentMessageId(null);
      return;
    }

    let disposed = false;
    setStatus('loading');
    setError(null);
    // CORE-620 — корень прошлого эфира не переживает смену эфира; повторный
    // заход после подписки (reloadTick) его не трогает, чтобы поле не мигало.
    const threadKey = `${channelKey}:${liveId}:${meta.discussionId ?? ''}`;
    if (threadKeyRef.current !== threadKey) {
      threadKeyRef.current = threadKey;
      setParentMessageId(null);
      setAllMessages([]);
    }

    // Membership → canWrite (subscribe-to-master adds the user to the circle).
    // CORE-1725 — запрос идёт с токеном (см. api.getMasterCircle): анонимно
    // сервер отвечает null, и CTA подписки видели все, включая владельца.
    // Упавший запрос — по-прежнему «не участник»: владельца это не задевает,
    // его узнаём по своему masterProfileId, а не по этому ответу.
    const answerKey = `${myAppId}:${meta.masterProfileId}`;
    api.getMasterCircle(meta.masterProfileId)
      .then((summary) => {
        if (!disposed) setMemberAnswer({ key: answerKey, isMember: summary?.isMember ?? null });
      })
      .catch(() => { if (!disposed) setMemberAnswer({ key: answerKey, isMember: false }); });

    // CORE-620 (актуализация 27.10) — сервер прямо сказал «карточки нет»: эфир
    // по ссылке или в программе (CORE-1764), повтор серии дальше 30 дней
    // (CORE-1283). Обсуждения нет ни для кого — канал не открываем вовсе.
    if (meta.discussionId === null) {
      setStatus('unavailable');
      return () => { disposed = true; };
    }

    const src = new ChatApiChannel(channelKey, myAppId);
    chatApiRef.current = src;

    // CORE-620 (актуализация 27.10) — где карточка события.
    //
    // `WatchChannel` отдаёт только новейшие 50 сообщений всего канала круга, а
    // в живом круге карточку эфира быстро вытесняют другие: на DEV у 38 из 39
    // ближайших эфиров после карточки лежало от 69 до 477 сообщений. Хук её не
    // находил и всё равно ставил «готово» — пустое обсуждение, поле ввода у
    // владельца (CORE-1725) и подписчика, а отправка падала с «Thread not ready».
    //
    // Теперь как в `EventThread` (CORE-1683, CORE-1685): нет карточки среди
    // новейших — листаем историю назад, пока не найдётся; найденный корень
    // помним и после переподключения тянем тред по нему одним запросом.
    // «Готово» — только с карточкой. Не нашли — «обсуждения нет».
    // Возврат QA 28.10: с известным `discussionId` — только по нему, иначе по
    // `eventId` вместе с видом события (см. `eventCardMatcher`).
    const isCard = eventCardMatcher({
      eventId: liveId,
      eventKind: meta.eventKind,
      discussionId: meta.discussionId,
    });
    const findCard = (all: ThreadMessage[]) => all.find(isCard);

    let cardId: string | null = null;
    let searching = false;
    // История пройдена, карточки нет — повторять незачем.
    let exhausted = false;
    // Поиск оборвался сбоем — повторяем на следующем событии канала
    // (переподключение перечитывает окно и зовёт sync).
    let failed = false;

    const ensureCard = async () => {
      if (searching || exhausted || disposed) return;
      searching = true;
      const outcome = await locateEventCard(src, () => !!findCard(src.getMessages()), cardId, () => disposed);
      searching = false;
      if (disposed) return;
      if (outcome === 'found') {
        failed = false;
        return;
      }
      // Карточка уже находилась — она есть, просто сейчас не дотянулись.
      if (outcome === 'failed' || cardId) {
        failed = true;
        if (!cardId) setStatus('error');
        return;
      }
      exhausted = true;
      setStatus('unavailable');
    };

    const sync = () => {
      if (disposed) return;
      const all = src.getMessages();
      setAllMessages(all);
      const card = findCard(all);
      if (card) {
        cardId = card.id;
        setParentMessageId(card.id);
        setStatus('ready');
        return;
      }
      // Карточка была и выпала: после переподключения и после каждой отправки
      // канал перечитывает те же новейшие 50. Корень известен — тред тянется
      // заново, а «готово» и корень ответа не сбрасываются.
      if (cardId || failed) void ensureCard();
    };

    src.onChange(sync);
    src.connect().then(() => {
      if (disposed) return;
      sync();
      if (cardId) return;
      void ensureCard();
    }).catch(() => {
      if (!disposed) {
        setError('Failed to load event thread');
        setStatus('error');
      }
    });

    return () => {
      disposed = true;
      chatApiRef.current = null;
      src.disconnect().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, myAppId, channelKey, liveId, discussionKey, reloadTick]);

  const card = useMemo(
    () => (parentMessageId ? allMessages.find((m) => m.id === parentMessageId) ?? null : null),
    [allMessages, parentMessageId],
  );
  const cardAuthorId = card?.senderId;

  const messages = useMemo<EventThreadMessage[]>(() => {
    if (!parentMessageId) return [];
    return allMessages
      .filter((m) => m.quoted?.id === parentMessageId)
      .map((m) => toThreadMessage(m, cardAuthorId));
  }, [allMessages, parentMessageId, cardAuthorId]);

  const pinnedMessage = useMemo<EventThreadMessage | null>(() => {
    if (card) {
      const description = (customOf(card).description as string | undefined) || '';
      const text = card.text || description;
      if (text) {
        return {
          id: card.id,
          author: card.senderName || 'Ведущий',
          photoUrl: card.senderAvatar ?? null,
          text,
          timestamp: formatTimestamp(card.createdAt),
          isHost: true,
          isOwn: card.isMine,
        };
      }
    }
    return messages.find((m) => m.isHost) || null;
  }, [card, messages]);

  // Comments never auto-freeze (parity with the legacy hook).
  const isFrozen = false;
  const membership = resolveDiscussionMembership({
    masterProfileId: meta?.masterProfileId,
    viewerMasterProfileId: user?.masterProfileId,
    isMember: memberAnswer && memberAnswer.key === `${myAppId}:${meta?.masterProfileId}`
      ? memberAnswer.isMember
      : null,
  });
  const isOwner = membership === 'owner';
  // `discussionId: null` известен и без входа: аноним тоже видит, что
  // обсуждения нет, а не предложение войти ради него.
  const readiness: DiscussionReadiness = meta?.discussionId === null
    ? 'unavailable'
    : status === 'error'
      ? 'error'
      : status === 'unavailable'
        ? 'unavailable'
        : enabled && status === 'ready' && parentMessageId
          ? 'ready'
          : 'loading';
  // CORE-620 — права (CORE-1725) и готовность вместе: владелец пишет без
  // подписки, но только в обсуждение, у которого есть карточка.
  const canWrite = canPostToDiscussion(membership, readiness);

  const sendMessage = useCallback(
    async (text: string) => {
      const src = chatApiRef.current;
      if (!src || !parentMessageId) throw new Error('Thread not ready');
      if (!canWrite) throw new Error('No write permission');
      const trimmed = text.trim();
      if (!trimmed) return;
      await src.send(trimmed, parentMessageId);
    },
    [parentMessageId, canWrite],
  );

  return {
    status: enabled ? status : 'idle',
    error,
    channelKey,
    parentMessageId,
    messages,
    pinnedMessage,
    participants: [],
    participantsCount: 0,
    isFrozen,
    canWrite,
    isOwner,
    membership,
    readiness,
    sendMessage,
  };
};
